import Dexie, { type Table } from 'dexie';
import type { PresentationPatch } from '../patches';

export interface UndoRedoState {
  id?: number;
  type: 'undo' | 'redo';
  slides: any[];
  selectedIndex: number;
  themeId?: string;
  changeType: 'slide' | 'theme' | 'both';
  panelState?: string;
  timestamp: number;
}

export interface PresentationBackup {
  id: string;
  slides: any[];
  rosTitle: string;
  layoutMode: string;
  teamOwnerId?: string;
  timestamp: string;
  version: string;
  deletedSlides: string[];
}

export interface StoredPatch {
  id?: number;
  presentationId: string;
  patch: PresentationPatch;
}

class StreamAlive extends Dexie {
  presentations!: Table<PresentationBackup, string>;
  history!: Table<UndoRedoState, number>;
  patches!: Table<StoredPatch, number>;

  constructor() {
    super('StreamAlive');
    this.version(2).stores({
      presentations: 'id, timestamp',
      history: '++id, type, timestamp',
    });
    this.version(3).stores({
      presentations: 'id, timestamp',
      history: '++id, type, timestamp',
      patches: '++id, presentationId',
    });
  }

  async clearHistory(): Promise<void> {
    await this.history.clear();
  }

  async clearAllPresentations(): Promise<void> {
    await this.presentations.clear();
  }

  async savePresentation(backup: PresentationBackup): Promise<void> {
    await this.presentations.put(backup);
  }

  async getPresentation(id: string): Promise<PresentationBackup | undefined> {
    return await this.presentations.get(id);
  }

  async deletePresentation(id: string): Promise<void> {
    await this.presentations.delete(id);
  }

  async addPatch(storedPatch: StoredPatch): Promise<number> {
    return await this.patches.add(storedPatch);
  }

  async getPatchesForPresentation(presentationId: string): Promise<StoredPatch[]> {
    return await this.patches.where('presentationId').equals(presentationId).toArray();
  }

  async clearPatchesForPresentation(presentationId: string): Promise<void> {
    await this.patches.where('presentationId').equals(presentationId).delete();
  }

  async pushHistory(
    type: 'undo' | 'redo',
    state: Omit<UndoRedoState, 'timestamp' | 'id' | 'type'>
  ): Promise<void> {
    await this.history.add({
      ...state,
      type,
      timestamp: Date.now()
    });
  }

  async getLatestHistory(type: 'undo' | 'redo'): Promise<UndoRedoState | undefined> {
    return await this.history
      .where('type')
      .equals(type)
      .reverse()
      .first();
  }

  async removeLatestHistory(type: 'undo' | 'redo'): Promise<UndoRedoState | undefined> {
    const latest = await this.getLatestHistory(type);
    if (latest && latest.id !== undefined) {
      await this.history.delete(latest.id);
    }
    return latest;
  }
}

export const db = new StreamAlive();
